import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  controleProducaoService,
  OrientacaoProducaoData,
  OrientacaoProducaoImagem,
} from "../../services/controleProducaoService";
import { orientacaoProducaoImageUrl } from "../../utils/orientacaoProducaoImageUrl";

const MAX_TEXTO = 300;
const MAX_IMAGENS = 4;

type NovaImagem = {
  file: File;
  previewUrl: string;
};

export type OrientacoesProducaoFormProps = {
  /** Loja cujas orientações (texto e imagens) serão editadas e exibidas. */
  lojaIdReferencia?: number;
  orientacao?: OrientacaoProducaoData | null;
  podeEditar: boolean;
  onSaved?: (orientacao: OrientacaoProducaoData) => void;
};

const OrientacoesProducaoForm: React.FC<OrientacoesProducaoFormProps> = ({
  lojaIdReferencia,
  orientacao,
  podeEditar,
  onSaved,
}) => {
  const [texto, setTexto] = useState("");
  const [imagensExistentes, setImagensExistentes] = useState<
    OrientacaoProducaoImagem[]
  >([]);
  const [novasImagens, setNovasImagens] = useState<NovaImagem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTexto(orientacao?.texto ?? "");
    setImagensExistentes(orientacao?.imagens ?? []);
    setNovasImagens((prev) => {
      prev.forEach((n) => URL.revokeObjectURL(n.previewUrl));
      return [];
    });
  }, [orientacao, lojaIdReferencia]);

  useEffect(
    () => () => {
      novasImagens.forEach((n) => URL.revokeObjectURL(n.previewUrl));
    },
    [novasImagens]
  );

  const totalImagens = imagensExistentes.length + novasImagens.length;
  const caracteresRestantes = MAX_TEXTO - texto.length;
  const podeAdicionarImagem = podeEditar && totalImagens < MAX_IMAGENS;

  const handleTextoChange = (value: string) => {
    if (value.length <= MAX_TEXTO) {
      setTexto(value);
    }
  };

  const handleSelecionarImagens = (files: FileList | null) => {
    if (!files || !podeEditar) return;
    const restantes = MAX_IMAGENS - totalImagens;
    if (restantes <= 0) {
      toast.warning(`Máximo de ${MAX_IMAGENS} imagens.`);
      return;
    }
    const selecionados = Array.from(files).slice(0, restantes);
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    for (const file of selecionados) {
      if (!allowed.includes(file.type)) {
        toast.error("Use imagens JPG, PNG ou WEBP.");
        continue;
      }
      setNovasImagens((prev) => [
        ...prev,
        { file, previewUrl: URL.createObjectURL(file) },
      ]);
    }
  };

  const removerExistente = (id: number) => {
    setImagensExistentes((prev) => prev.filter((img) => img.id !== id));
  };

  const removerNova = (index: number) => {
    setNovasImagens((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const salvar = async () => {
    if (!podeEditar) return;
    if (lojaIdReferencia == null || lojaIdReferencia < 1) {
      toast.warning("Selecione uma loja antes de salvar as orientações.");
      return;
    }
    setSaving(true);
    try {
      const saved = await controleProducaoService.saveOrientacaoProducao({
        lojaId: lojaIdReferencia,
        texto,
        imagensManter: imagensExistentes.map((img) => img.id),
        novasImagens: novasImagens.map((n) => n.file),
      });
      toast.success("Orientações salvas.");
      onSaved?.(saved);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao salvar orientações.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const vazio = useMemo(
    () => !texto.trim() && totalImagens === 0,
    [texto, totalImagens]
  );

  return (
    <section className="mt-6 rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-neutral-900">
          Orientações
        </h2>
        {!podeEditar && (
          <span className="text-xs text-neutral-500">
            Somente administradores podem editar
          </span>
        )}
      </div>
      <p className="mb-3 text-xs text-neutral-500">
        Orientação específica desta loja. O texto e as imagens são exibidos na
        base semanal, na ficha diária e na impressão desta unidade.
      </p>

      <label className="block text-sm font-medium text-neutral-700">
        Texto (até {MAX_TEXTO} caracteres)
        <textarea
          value={texto}
          onChange={(e) => handleTextoChange(e.target.value)}
          readOnly={!podeEditar}
          rows={3}
          placeholder="Instruções operacionais para a equipe…"
          className={`mt-1 w-full resize-y rounded-md border border-neutral-300 px-3 py-2 text-sm ${
            !podeEditar ? "cursor-default bg-neutral-50" : ""
          }`}
        />
        {podeEditar && (
          <span
            className={`mt-1 block text-xs ${
              caracteresRestantes < 20 ? "text-amber-700" : "text-neutral-500"
            }`}
          >
            {caracteresRestantes} caracteres restantes
          </span>
        )}
      </label>

      <div className="mt-4">
        <p className="text-sm font-medium text-neutral-700">
          Imagens (até {MAX_IMAGENS})
        </p>
        <div className="mt-2 flex flex-wrap gap-3">
          {imagensExistentes.map((img) => (
            <div key={img.id} className="relative">
              <img
                src={orientacaoProducaoImageUrl(img.url)}
                alt={`Orientação ${img.ordem}`}
                className="h-[250px] w-[250px] rounded-md border border-neutral-200 object-contain"
              />
              {podeEditar && (
                <button
                  type="button"
                  onClick={() => removerExistente(img.id)}
                  className="absolute right-1 top-1 rounded bg-black/60 px-2 py-0.5 text-xs font-semibold text-white"
                  aria-label="Remover imagem"
                >
                  Remover
                </button>
              )}
            </div>
          ))}
          {novasImagens.map((img, index) => (
            <div key={img.previewUrl} className="relative">
              <img
                src={img.previewUrl}
                alt={`Nova imagem ${index + 1}`}
                className="h-[250px] w-[250px] rounded-md border border-dashed border-neutral-300 object-contain"
              />
              {podeEditar && (
                <button
                  type="button"
                  onClick={() => removerNova(index)}
                  className="absolute right-1 top-1 rounded bg-black/60 px-2 py-0.5 text-xs font-semibold text-white"
                  aria-label="Remover imagem nova"
                >
                  Remover
                </button>
              )}
            </div>
          ))}
        </div>
        {podeAdicionarImagem && (
          <label className="mt-3 inline-flex cursor-pointer items-center rounded-md border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
            Adicionar imagens
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/jpg"
              multiple
              className="sr-only"
              onChange={(e) => {
                handleSelecionarImagens(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
        )}
        <p className="mt-1 text-xs text-neutral-500">
          {totalImagens}/{MAX_IMAGENS} imagens · JPG, PNG ou WEBP
        </p>
      </div>

      {podeEditar && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => void salvar()}
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? "Salvando..." : "Salvar orientações"}
          </button>
        </div>
      )}

      {!podeEditar && vazio && (
        <p className="mt-2 text-sm text-neutral-500">
          Nenhuma orientação cadastrada.
        </p>
      )}
    </section>
  );
};

export default OrientacoesProducaoForm;
